import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Badge,
  Box,
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  LoadingRow,
  QuietRow,
  ScrollBand,
  SectionLabel,
  Skeleton,
  SkeletonGroup,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
} from "@salestrak/ui";
import React, { useMemo, useState } from "react";
import type { Color, GlyphName, Omittable } from "@salestrak/ui";
import type { ObjectAccessLevel } from "#app/lib/data";
import { useRolePool } from "#app/components/entity-selectors/hooks/use-role-pool";
import {
  EntityGroupTag,
  EntityVisual,
  useYieldToEntityDetail,
} from "#app/components/entity";
import { resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { validateEmail } from "#app/lib/custom-field-utils";
import { useCopyFeedback } from "#app/lib/utils/copy-actions";
import { useWorkspace } from "#app/lib/data/app-provider";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { useInvitableRoles } from "#app/lib/hooks/use-invitable-roles";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import type {
  AccessSummaryMember,
  ObjectSharing,
} from "#app/lib/hooks/use-object-sharing";
import { useWorkspaceRoster } from "#app/lib/hooks/use-workspace-roster";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { getDisplayName } from "#app/lib/utils/get-display-name";
import { memberCountLabel } from "#app/lib/utils/member-count";
import {
  ACCESS_LEVEL_LABEL,
  AccessLevelMenu,
  EditAllMenu,
} from "./access-level-menu";
import {
  ACCESS_BAND_PADDING,
  ACCESS_ROW_HEIGHT,
  AccessRowFrame,
} from "./access-row";
import {
  memberEntity,
  teamEntity,
  roleBadge,
  memberMeta,
  HeaderRow,
  LockedLevel,
  StaticLevel,
  NESTED_ROW_HEIGHT,
} from "./share-rows";
import { TeamAccessRow } from "./team-access-rows";
import { ResultRow } from "./share-result-row";
import { InviteForm } from "./invite-form";
import { SkeletonRow, ShareListSkeleton } from "./share-dialog-skeleton";
import { SharingEvent } from "#app/lib/analytics";

// Generic presentational share dialog used for workflows, items and
// workspace dashboards. The wrapping component (workflow-share-dialog /
// item-share-dialog / dashboard-share-dialog) owns the GraphQL hook
// (`useObjectSharing`) and the subject; this file renders. One exception:
// the invite-by-email path lives here (member invites are object-agnostic)
// — the per-object share grant still flows through the hook's
// `setMemberShare`.
//
// The dialog reads top to bottom as: what is being shared (the subject is
// the title, with the reach under it), how do I add someone (the field),
// and who has access: one list of principals with no label over it. The
// workspace leads it as a row of its own — the subject's name with a
// "Workspace members" badge, the tier public visibility grants, and the
// visibility switch — and opens into the people who reach the subject that
// way. Under it, one collapsible "Shared with" group holds everyone added
// by name, teams first. Every row a share can change carries the same
// pair: a tier menu and a switch. The footer is one button bound to the
// workspace row's switch. The UI docs Sharing pattern page is the source
// of this shape.


// A band shows six rows before it scrolls: enough to read as a list,
// short enough that the dialog never grows past the viewport.
const BAND_MAX_HEIGHT = ACCESS_ROW_HEIGHT * 6 + ACCESS_BAND_PADDING;
// How close to a band's bottom edge the next page loads.
const LOAD_MORE_THRESHOLD_PX = 80;

// The thing being shared, named as the title. An entity renders as its
// chip; a saved view (a workspace dashboard) is not an entity kind, so it
// carries its own glyph and name.
export type ShareSubject =
  | EntityData
  | { kind: "view"; icon: GlyphName; color: Color; name: string };

export type ShareDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Null while the wrapper is still resolving the subject (the title's
  // slot shows a bar).
  subject: ShareSubject | null;
  // Lower-case noun for the copy: "workflow", "project", "account".
  subjectNoun: string;
  // The line under the title naming the kind and the reach ("Sharing
  // workflow with all views"). Defaults to "Sharing <noun>".
  description?: string;
  // The subject's deep link, for the Link row under the add field. Omit
  // while the wrapper cannot build one; the row then stays out.
  shareUrl?: string | null;
  sharing: ObjectSharing;
  // Drives the debounced search box's onValueChange so the wrapping
  // hook re-queries with the active search term.
  onSearchChange: (value: string | undefined) => void;
}>;

export function ShareDialog({
  open,
  onOpenChange,
  subject,
  subjectNoun,
  description,
  shareUrl,
  sharing,
  onSearchChange,
}: ShareDialogProps) {
  const {
    summary,
    loading,
    hasMoreMembers,
    loadMoreMembers,
    hasMoreTeams,
    loadMoreTeams,
    setMemberShare,
    setTeamShare,
    unshare,
    setVisibility,
    setPublicAccessLevel,
    setAll,
    removeAll,
    teamMembers,
    workspaceRoster,
    trackSharing,
  } = sharing;
  // One `sharing.open` per open.
  React.useEffect(() => {
    if (open) trackSharing(SharingEvent.Open);
  }, [open, trackSharing]);
  const { preferences } = useUserSettings();
  const displayFullNames = preferences?.displayFullNames ?? false;
  const { activeWorkspace } = useWorkspace();
  // On a phone the dialog is a sheet, and sheet buttons are round; the
  // squared verbs are the desktop dialog's.
  const isMobile = useIsMobile();
  const inviteShape = isMobile ? "pill" : "square";

  // A team picker's "Team" row action navigates to a team page — a modal
  // left open would sit over it, so this dialog yields (the app convention
  // for modals whose content can open an entity detail).
  useYieldToEntityDetail(open, () => onOpenChange(false));

  const [searchInput, setSearchInput] = useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebouncedValue(searchInput, 200);
  // Clearing keeps focus in the field so the next name can be typed at once.
  const clearSearch = React.useCallback(() => {
    setSearchInput("");
    searchRef.current?.focus();
  }, []);
  const activeSearch =
    debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : undefined;

  React.useEffect(() => {
    onSearchChange(activeSearch);
  }, [activeSearch, onSearchChange]);

  // Both groups start closed: someone who opens one is checking access,
  // someone who leaves them closed is adding people.
  const [openSections, setOpenSections] = useState<string[]>([]);
  const workspaceOpen = openSections.includes("workspace");
  const sharedOpen = openSections.includes("shared");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmPrivate, setConfirmPrivate] = useState(false);

  // ── Invite-by-email path ──
  // When the search term is a valid email that matches no workspace member
  // (the add path lists ALL members, search-filtered server-side), the body
  // becomes an invite form: role + access level + teams, and the footer
  // becomes Cancel / Send invite.
  const { inviteMembers } = useMemberMutations();
  const workspaceId = activeWorkspace?.workspaceId ?? "";
  const { allowedRoleIds } = useInvitableRoles(workspaceId);
  const { defaultRoleId } = useRolePool({ allowedRoleIds });

  const [inviteRole, setInviteRole] = useState<string | null>(null);
  const [inviteTeamIds, setInviteTeamIds] = useState<string[]>([]);
  const [inviteAccess, setInviteAccess] = useState<ObjectAccessLevel>("edit");
  const [sendingInvite, setSendingInvite] = useState(false);
  const inviteRoleId = inviteRole ?? defaultRoleId;

  const resetInviteState = React.useCallback(() => {
    setInviteRole(null);
    setInviteTeamIds([]);
    setInviteAccess("edit");
  }, []);

  // Reset search, invite form and the open groups when the dialog closes so
  // reopening on another subject starts from a clean state.
  React.useEffect(() => {
    if (!open) {
      setSearchInput("");
      setOpenSections([]);
      resetInviteState();
    }
  }, [open, resetInviteState]);

  const inviteEmail =
    activeSearch && validateEmail(activeSearch) ? activeSearch : null;
  const inviteMode =
    !!inviteEmail &&
    !loading &&
    summary != null &&
    summary.filteredMemberCount === 0 &&
    summary.filteredTeamCount === 0;

  const handleSendInvite = React.useCallback(async () => {
    if (!inviteEmail || sendingInvite || inviteRoleId === null) return;
    setSendingInvite(true);
    const { succeededUsers } = await inviteMembers([
      { email: inviteEmail, roleId: inviteRoleId, teamIds: inviteTeamIds },
    ], "share_dialog");
    setSendingInvite(false);
    const invited = succeededUsers.find(
      (u) => u.email.toLowerCase() === inviteEmail.toLowerCase(),
    );
    // Failure toasts are shown by the hook — stay on the form for retry.
    if (!invited) return;
    // Grant the new member access to this workflow/item at the chosen
    // level — writes the workflow_share / item_share row via the hook.
    setMemberShare(invited.userId, inviteAccess, true);
    // Back to the access list; the share mutation's refetch lists the new
    // invited-member row.
    setSearchInput("");
    resetInviteState();
  }, [
    inviteEmail,
    sendingInvite,
    inviteRoleId,
    inviteTeamIds,
    inviteAccess,
    inviteMembers,
    setMemberShare,
    resetInviteState,
  ]);

  const cancelInvite = React.useCallback(() => {
    clearSearch();
    resetInviteState();
  }, [clearSearch, resetInviteState]);

  const teams = useMemo(() => summary?.teams ?? [], [summary]);
  const members = useMemo(() => summary?.members ?? [], [summary]);
  // The owner and admins hold access through their role, not through the
  // list: they lead the workspace row's roster with a static tier and are
  // never in the "Shared with" group.
  const lockedMembers = useMemo(() => members.filter((m) => m.locked), [members]);
  const sharedMembers = useMemo(() => members.filter((m) => !m.locked), [members]);
  const isPublic = summary?.visibility === "public";
  const publicLevel = summary?.publicAccessLevel ?? "view";
  const lockedCount = summary?.lockedMemberCount ?? 0;
  const changeable = teams.length + sharedMembers.length;
  // The people who reach the subject through the workspace alone: making it
  // private takes access from every one of them at once, so that flip asks
  // first and names the count. Opening it up, or a private flip that strands
  // no one, applies at once.
  const workspaceOnlyCount = summary?.publicAccessMemberCount ?? 0;
  const requestVisibility = (next: "public" | "private") => {
    if (next === "private" && isPublic && workspaceOnlyCount > 0) {
      setConfirmPrivate(true);
      return;
    }
    setVisibility(next);
  };

  // The people who reach the subject through the workspace alone, loaded
  // while the row is open and the dialog is at rest.
  const roster = useWorkspaceRoster(
    workspaceRoster,
    open && workspaceOpen && !activeSearch,
  );

  // The closed group's face stack: only rows that are switched on.
  const faces = useMemo(
    (): EntityData[] => [
      ...teams.filter((t) => t.enabled).map(teamEntity),
      ...sharedMembers.filter((m) => m.enabled).map(memberEntity),
    ],
    [teams, sharedMembers],
  );

  // Each band is its own scroll region; whichever list still has pages
  // loads its next one as the bottom approaches.
  const nearBottom = (target: HTMLElement) =>
    target.scrollHeight - target.scrollTop - target.clientHeight <
    LOAD_MORE_THRESHOLD_PX;
  const handleSharedScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreMembers && !hasMoreTeams) return;
    if (!nearBottom(e.currentTarget)) return;
    if (hasMoreTeams) loadMoreTeams();
    else loadMoreMembers();
  };
  const handleRosterScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (roster.hasMore && nearBottom(e.currentTarget)) roster.loadMore();
  };
  // The results list scrolls with the panel.
  const handleResultsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!activeSearch || (!hasMoreMembers && !hasMoreTeams)) return;
    if (!nearBottom(e.currentTarget)) return;
    if (hasMoreTeams) loadMoreTeams();
    else loadMoreMembers();
  };

  const memberLabel = (m: AccessSummaryMember) =>
    getDisplayName(m, displayFullNames) ?? "—";

  const subjectName = subject
    ? subject.kind === "view"
      ? subject.name
      : resolveEntityLabel(subject)
    : "";

  return (
    // The phone sheet opens full: the body is worked in (a member search
    // over the access list, the link row and the visibility switch), and a
    // half sheet showed a search field with two rows under it.
    <Dialog open={open} onOpenChange={onOpenChange} sheetSize="full">
      <DialogPopup initialFocus={false}>
        <DialogHeader>
          {/* The subject is the title, named the way it reads everywhere
              else. No "Share" verb above it — the trigger said it, and the
              rows say the rest. The line under it names the kind and the
              reach. */}
          {/* The close pill sits 16px inside the header's inset, so a
              one-line title clears it with a step of its own: the name
              ends before the pill instead of under it. */}
          <DialogTitle
            render={
              <Box direction="row" align="center" gap="sm" minWidth={0} paddingRight="lg" />
            }
          >
            {/* The glyph beside the title's own type: a 20px mark in a 20px
                box next to 20px text, the same rhythm as a row's tag, never
                a tile that grows the header. A long name keeps the title on
                one line and hands the rest to an overflow tooltip, so the
                header never grows and the name is still readable. */}
            {subject ? (
              <>
                {subject.kind === "view" ? (
                  <Icon name={subject.icon} color={subject.color} size="md" />
                ) : (
                  <EntityVisual entity={subject} size="md" boxSize={20} avatarSize="xs" />
                )}
                <Text
                  truncate={1}
                  overflowTooltip
                  size="xl"
                  weight="semibold"
                  className="font-heading leading-none"
                >
                  {subjectName}
                </Text>
              </>
            ) : (
              <Skeleton width={160} height={20} delayed />
            )}
          </DialogTitle>
          <DialogDescription>
            {description ?? `Sharing ${subjectNoun}`}
          </DialogDescription>
        </DialogHeader>

        <DialogPanel onScroll={handleResultsScroll}>
          <Box gap="base">
            {/* The add field: members and teams by name, or an email to
                invite. The same recipe as `ExpandableSearch`. */}
            <InputGroup>
              <InputGroupAddon>
                <Icon name="search" size="sm" color="muted" />
              </InputGroupAddon>
              <InputGroupInput
                ref={searchRef}
                aria-label="Add people, teams, or an email"
                placeholder="Add people, teams, or an email"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                // Escape with a term clears the term; only an empty field lets
                // it through to close the dialog.
                onKeyDown={(event) => {
                  if (event.key === "Escape" && searchInput) {
                    event.preventDefault();
                    event.stopPropagation();
                    clearSearch();
                  }
                }}
              />
              {searchInput ? (
                <InputGroupAddon align="inline-end">
                  {/* The library search's clear chip: a round ghost × at the
                      field's trailing cap (ExpandableSearch's recipe). */}
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="rounded-full [&_svg]:-translate-x-[0.333px]"
                    aria-label="Clear search"
                    onClick={clearSearch}
                  >
                    <Icon name="x" />
                  </Button>
                </InputGroupAddon>
              ) : null}
            </InputGroup>

            {inviteMode && inviteEmail ? (
              <InviteForm
                email={inviteEmail}
                roleId={inviteRoleId}
                onRoleChange={setInviteRole}
                allowedRoleIds={allowedRoleIds}
                access={inviteAccess}
                onAccessChange={setInviteAccess}
                teamIds={inviteTeamIds}
                onTeamIdsChange={setInviteTeamIds}
              />
            ) : sharing.failed ? (
              <Box direction="row" align="center" justify="between" gap="sm">
                <QuietRow height={ACCESS_ROW_HEIGHT}>
                  Could not load who has access.
                </QuietRow>
                <Button variant="ghost" shape={inviteShape} onClick={sharing.retry}>
                  Try again
                </Button>
              </Box>
            ) : loading || !summary ? (
              <ShareListSkeleton hasLink={!!shareUrl} />
            ) : activeSearch ? (
              <Box gap="2xs">
                <Box height={32} justify="center">
                  <SectionLabel>Results</SectionLabel>
                </Box>
                {teams.length === 0 && members.length === 0 ? (
                  <QuietRow height={ACCESS_ROW_HEIGHT}>
                    {sharing.searching
                      ? "Searching…"
                      : "No members or teams match"}
                  </QuietRow>
                ) : (
                  <Box>
                    {teams.map((team) => (
                      <ResultRow
                        key={team.id}
                        entity={teamEntity(team)}
                        label={team.name}
                        meta={
                          team.enabled
                            ? team.source === "team"
                              ? "On this Project"
                              : "Has access"
                            : memberCountLabel(team.memberCount)
                        }
                        granted={team.enabled}
                        level={team.accessLevel}
                        onAdd={() => setTeamShare(team.id, team.accessLevel, true)}
                        onChange={(level) => setTeamShare(team.id, level, true)}
                        onRevoke={
                          team.source === "team"
                            ? undefined
                            : () => setTeamShare(team.id, team.accessLevel, false)
                        }
                      />
                    ))}
                    {members.map((member) => (
                      <ResultRow
                        key={member.id}
                        entity={memberEntity(member)}
                        label={memberLabel(member)}
                        role={roleBadge(member)}
                        meta={
                          member.locked
                            ? undefined
                            : member.enabled
                              ? "Has access"
                              : undefined
                        }
                        granted={member.enabled}
                        locked={member.locked}
                        level={member.accessLevel}
                        onAdd={() =>
                          setMemberShare(member.id, member.accessLevel, true)
                        }
                        onChange={(level) =>
                          setMemberShare(member.id, level, true)
                        }
                        onRevoke={() =>
                          setMemberShare(member.id, member.accessLevel, false)
                        }
                      />
                    ))}
                    {hasMoreMembers || hasMoreTeams ? <LoadingRow height={ACCESS_ROW_HEIGHT} /> : null}
                  </Box>
                )}
              </Box>
            ) : (
              /* The accordion bleeds past the panel's inset so its washes and
                 bands run edge to edge; each header and band pads the rows
                 back onto the panel's column. The wrapper shuts its
                 horizontal overflow: an accordion panel reaches 4px past its
                 parent on each side to keep a focus ring whole, and flush
                 with the dialog's edge those 4px would otherwise give the
                 dialog a sideways scrollbar. */
              <Box marginXNegative="lg" overflowX="hidden">
                {/* Sharing by name is the field; sharing by link is the
                    first row of the list, on the list's own rhythm. */}
                {shareUrl ? (
                  <LinkRow
                    url={shareUrl}
                    onCopied={() => trackSharing(SharingEvent.CopyLink)}
                    reach={
                      isPublic
                        ? "Workspace members can open it"
                        : "Only people with access can open it"
                    }
                  />
                ) : null}
                <Accordion
                  multiple
                  value={openSections}
                  onValueChange={setOpenSections}
                >
                  <AccordionItem separator={false} value="workspace">
                    <HeaderRow>
                      <AccordionTrigger indicator="disclosure" size="sm">
                        {/* The row is named for the subject, with no mark
                            of its own: the title above carries the glyph.
                            The badge names who reaches the subject through
                            it. Off, who still does waits behind the same
                            small muted info glyph as the link row: an
                            inline line here wrapped and truncated the
                            name at the dialog's width. The badge is
                            rigid; only the name gives way. */}
                        <Box direction="row" align="center" gap="sm" minWidth={0}>
                          {/* The name keeps a floor of a few words and hands
                              the rest to an overflow tooltip, so a long name
                              reads "Test Workfl…  Workspace members" and the
                              full name is one hover away. */}
                          <Box minWidth={80} shrink>
                            <Text
                              size="sm"
                              truncate={1}
                              overflowTooltip
                              variant={isPublic ? undefined : "muted"}
                            >
                              {subjectName}
                            </Text>
                          </Box>
                          <Box shrink={false} direction="row" align="center" gap="xs">
                            <Badge size="sm">Workspace members</Badge>
                            {isPublic ? null : (
                              <Tooltip>
                                <TooltipTrigger
                                  render={<Box aria-label={keepAccessCopy(lockedCount)} />}
                                >
                                  <Icon name="info" size="xs" color="muted" />
                                </TooltipTrigger>
                                <TooltipPopup side="top">
                                  {keepAccessCopy(lockedCount)}
                                </TooltipPopup>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </AccordionTrigger>
                      <Box
                        direction="row"
                        align="center"
                        gap="sm"
                        paddingRight="sm"
                        shrink={false}
                      >
                        {setPublicAccessLevel ? (
                          <AccessLevelMenu
                            value={publicLevel}
                            onChange={setPublicAccessLevel}
                            disabled={!isPublic}
                          />
                        ) : (
                          /* An item has no tier of its own — everyone
                             reaches it through its workflow at View. */
                          <StaticLevel
                            label={ACCESS_LEVEL_LABEL[publicLevel]}
                            hint={`Workspace members reach a ${subjectNoun} at ${ACCESS_LEVEL_LABEL[publicLevel].toLowerCase()}`}
                          />
                        )}
                        <Switch
                          aria-label="Open to workspace members"
                          checked={isPublic}
                          onCheckedChange={(on) =>
                            requestVisibility(on ? "public" : "private")
                          }
                          size="sm"
                        />
                      </Box>
                    </HeaderRow>
                    <AccordionPanel padding="none">
                      <ScrollBand maxHeight={BAND_MAX_HEIGHT} onScroll={handleRosterScroll}>
                        {/* Role holders first, static: the tier is the role's. */}
                        {lockedMembers.map((member) => (
                          <AccessRowFrame
                            key={member.id}
                            entity={memberEntity(member)}
                            label={memberLabel(member)}
                            role={roleBadge(member)}
                            height={NESTED_ROW_HEIGHT}
                          >
                            <LockedLevel />
                          </AccessRowFrame>
                        ))}
                        {/* Members reach the subject through the workspace at
                            its tier, read here in muted ink. Changing one —
                            a different tier, or switched off — writes a
                            share of their own, and they move to the group
                            below. */}
                        {roster.loading ? (
                          <SkeletonGroup>
                            {[0, 1, 2].map((i) => (
                              <SkeletonRow key={i} height={NESTED_ROW_HEIGHT} />
                            ))}
                          </SkeletonGroup>
                        ) : (
                          roster.members.map((member) => (
                            <AccessRowFrame
                              key={member.id}
                              entity={memberEntity(member)}
                              label={memberLabel(member)}
                              height={NESTED_ROW_HEIGHT}
                            >
                              <AccessLevelMenu
                                value={publicLevel}
                                inherited
                                onChange={(level) =>
                                  setMemberShare(member.id, level, true)
                                }
                              />
                              <Switch
                                aria-label={`${memberLabel(member)} has access`}
                                checked
                                onCheckedChange={() =>
                                  setMemberShare(member.id, publicLevel, false)
                                }
                                size="sm"
                              />
                            </AccessRowFrame>
                          ))
                        )}
                        {roster.hasMore ? <LoadingRow height={NESTED_ROW_HEIGHT} /> : null}
                      </ScrollBand>
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem separator={false} value="shared">
                    <HeaderRow>
                      <AccordionTrigger indicator="disclosure" size="sm">
                        <Text size="sm" weight="medium">
                          Shared with
                        </Text>
                      </AccordionTrigger>
                      <Box
                        direction="row"
                        align="center"
                        gap="sm"
                        paddingRight="sm"
                        shrink={false}
                      >
                        {/* Closed, a small stack of tiles and faces; open,
                            the bulk menu, because the rows one line down
                            show the faces. The menu appears once two or
                            more rows can change. */}
                        {!sharedOpen && faces.length > 0 ? (
                          <EntityGroupTag
                            entities={faces}
                            max={3}
                            size="xs"
                            interactive={false}
                            hoverDisabled
                          />
                        ) : null}
                        {sharedOpen && changeable >= 2 ? (
                          <EditAllMenu
                            onSetAll={(level) => void setAll(level)}
                            onRemoveAll={() => setConfirmRemove(true)}
                          />
                        ) : null}
                      </Box>
                    </HeaderRow>
                    <AccordionPanel padding="none">
                      <ScrollBand maxHeight={BAND_MAX_HEIGHT} onScroll={handleSharedScroll}>
                        {changeable === 0 ? (
                          <QuietRow height={ACCESS_ROW_HEIGHT}>
                            Nothing shared yet. Add people or teams above.
                          </QuietRow>
                        ) : null}
                        {teams.map((team) => (
                          <TeamAccessRow
                            key={team.id}
                            team={team}
                            source={teamMembers}
                            memberLabel={memberLabel}
                            onChange={(level) => setTeamShare(team.id, level, true)}
                            onToggle={(on) =>
                              setTeamShare(team.id, team.accessLevel, on)
                            }
                            onMemberChange={(id, level) =>
                              setMemberShare(id, level, true)
                            }
                            onMemberDeny={(id, level) =>
                              setMemberShare(id, level, false)
                            }
                            onMemberReset={(id) => unshare("user", id)}
                          />
                        ))}
                        {sharedMembers.map((member) => (
                          <AccessRowFrame
                            key={member.id}
                            entity={memberEntity(member)}
                            label={memberLabel(member)}
                            role={roleBadge(member)}
                            meta={memberMeta(member)}
                            dimmed={!member.enabled}
                          >
                            <AccessLevelMenu
                              value={member.accessLevel}
                              disabled={!member.enabled}
                              onChange={(level) =>
                                setMemberShare(member.id, level, true)
                              }
                            />
                            <Switch
                              aria-label={`${memberLabel(member)} has access`}
                              checked={member.enabled}
                              onCheckedChange={(on) =>
                                setMemberShare(member.id, member.accessLevel, on)
                              }
                              size="sm"
                            />
                          </AccessRowFrame>
                        ))}
                        {hasMoreMembers || hasMoreTeams ? <LoadingRow height={ACCESS_ROW_HEIGHT} /> : null}
                      </ScrollBand>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </Box>
            )}
          </Box>
        </DialogPanel>

        {/* Every verb in this dialog is squared: the rows' tier menus are,
            and a pill beside them read as a different control. The invite
            pair rounds on a phone, where the dialog is a sheet. */}
        <DialogFooter variant="bare">
          {inviteMode && inviteEmail ? (
            <>
              <Button
                variant="ghost"
                shape={inviteShape}
                onClick={cancelInvite}
                disabled={sendingInvite}
              >
                Cancel
              </Button>
              <Button
                shape={inviteShape}
                onClick={handleSendInvite}
                disabled={sendingInvite || inviteRoleId === null}
                loading={sendingInvite}
              >
                <Icon name="mail" size="sm" />
                <Text>Send invite</Text>
              </Button>
            </>
          ) : summary ? (
            /* Bound to the workspace row's switch: pressing either flips
               both. The switch shows the state where the row lives; the
               button names the flip as a verb at the bottom. */
            <Button
              fullWidth
              variant="outline"
              shape="square"
              onClick={() => requestVisibility(isPublic ? "private" : "public")}
            >
              <Icon name={isPublic ? "lock" : "globe"} size="sm" />
              {isPublic ? "Make private" : "Make public"}
            </Button>
          ) : (
            <SkeletonGroup>
              <Skeleton width="100%" height={32} />
            </SkeletonGroup>
          )}
        </DialogFooter>
      </DialogPopup>

      <AlertDialog open={confirmPrivate} onOpenChange={setConfirmPrivate}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Make this {subjectNoun} private?</AlertDialogTitle>
            <AlertDialogDescription>
              {workspaceOnlyCount === 1
                ? `1 person loses access to this ${subjectNoun}: they reach it only as a workspace member.`
                : `${workspaceOnlyCount.toLocaleString()} people lose access to this ${subjectNoun}: they reach it only as workspace members.`}{" "}
              Shared teams and people, the owner and admins keep theirs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter variant="bare">
            <AlertDialogClose render={<Button variant="outline" />}>
              Keep it open
            </AlertDialogClose>
            <AlertDialogClose
              onClick={() => setVisibility("private")}
              render={<Button color="destructive" />}
            >
              Make private
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove everyone's access?</AlertDialogTitle>
            <AlertDialogDescription>
              {changeable === 1
                ? `The one shared team or person loses access to this ${subjectNoun}.`
                : `All ${changeable} shared teams and people lose access to this ${subjectNoun}.`}{" "}
              The owner and admins keep theirs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter variant="bare">
            <AlertDialogClose render={<Button variant="outline" />}>
              Keep access
            </AlertDialogClose>
            <AlertDialogClose
              onClick={() => void removeAll()}
              render={<Button color="destructive" />}
            >
              Remove all
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Dialog>
  );
}

// ─── Link row ────────────────────────────────────────────────────────

// The subject's link as the first row of the list: the same frame, column
// and pitch as the workspace row under it, with no wash because nothing
// opens. "Link" leads at the rows' size with the reach behind a small
// muted info glyph's tooltip (an inline line was too much for the row), and
// the verb trails on the controls' edge as a squared outline button on the
// tier menus' 28px rung. It replaces a 24px icon in the close pill's
// corner, which was the smallest target in the dialog for its most-pressed
// action. The press flashes "Copied" in place for a beat, beside the toast,
// so the feedback lands under the pointer; the button keeps one width
// across both labels so nothing beside it shifts.
function LinkRow({
  url,
  reach,
  onCopied,
}: {
  url: string;
  reach: string;
  onCopied: () => void;
}) {
  const { copied, copy } = useCopyFeedback();
  return (
    <HeaderRow hover={false}>
      <Box direction="row" align="center" gap="xs" minWidth={0}>
        <Text size="sm">Link</Text>
        {/* The reach behind a small muted info glyph: the row says "Link"
            and who it opens for waits on hover, so the row stays one
            word wide at rest. */}
        <Tooltip>
          <TooltipTrigger render={<Box role="img" aria-label={reach} />}>
            <Icon name="info" size="xs" color="muted" />
          </TooltipTrigger>
          <TooltipPopup side="top">{reach}</TooltipPopup>
        </Tooltip>
      </Box>
      <Box paddingRight="sm" shrink={false}>
        <Button
          size="sm"
          variant="outline"
          shape="square"
          className="min-w-24"
          onClick={async () => {
            if (await copy(url, "Link")) onCopied();
          }}
        >
          <Icon name={copied ? "check" : "link"} size="sm" />
          <Text>{copied ? "Copied" : "Copy link"}</Text>
        </Button>
      </Box>
    </HeaderRow>
  );
}

// ─── Copy ────────────────────────────────────────────────────────────

// Who still reaches a restricted subject on their own. `lockedCount` is
// the owner plus every admin whose role reaches it.
function keepAccessCopy(lockedCount: number): string {
  const admins = Math.max(0, lockedCount - 1);
  if (admins === 0) return "Owner keeps access";
  return `Owner and ${admins} ${admins === 1 ? "admin" : "admins"} keep access`;
}
